import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  ViewList,
  ViewModule,
  CalendarMonth,
  Timeline,
  BarChart,
  TableChart,
  GridOn,
} from '@mui/icons-material';

/**
 * Reusable View Toggle Component
 * Allows switching between different view modes (list, kanban, calendar, etc.)
 * 
 * @param {string} viewMode - Current active view mode
 * @param {function} onViewModeChange - Callback when view mode changes
 * @param {array} viewModes - Array of available view modes
 * @param {object} sx - Additional styles
 */

const viewModeIcons = {
  list: { icon: ViewList, tooltip: 'List view' },
  kanban: { icon: ViewModule, tooltip: 'Kanban view' },
  calendar: { icon: CalendarMonth, tooltip: 'Calendar view' },
  timeline: { icon: Timeline, tooltip: 'Timeline view' },
  chart: { icon: BarChart, tooltip: 'Chart view' },
  table: { icon: TableChart, tooltip: 'Table view' },
  grid: { icon: GridOn, tooltip: 'Grid view' },
};

const ViewToggle = ({
  viewMode = 'list',
  onViewModeChange,
  viewModes = ['list', 'kanban'],
  sx = {},
}) => {
  if (!viewModes || viewModes.length <= 1) {
    return null;
  }

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        overflow: 'hidden',
        height: 32,
        ...sx,
      }}
    >
      {viewModes.map((mode) => {
        const config = viewModeIcons[mode] || { icon: ViewList, tooltip: mode };
        const IconComponent = config.icon;
        const isActive = viewMode === mode;

        return (
          <Tooltip key={mode} title={config.tooltip}>
            <IconButton
              size="small"
              onClick={() => onViewModeChange(mode)}
              sx={{
                borderRadius: 0,
                bgcolor: isActive ? '#e9ecef' : 'transparent',
                color: isActive ? '#495057' : '#999',
                px: 1,
                py: 0.75,
                '&:hover': {
                  bgcolor: isActive ? '#e9ecef' : '#f8f9fa',
                },
                borderRight: mode !== viewModes[viewModes.length - 1] ? '1px solid #dee2e6' : 'none',
              }}
            >
              <IconComponent sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default ViewToggle;
